import { useEffect, useCallback, useState } from 'react';
import { useRouter } from 'next/compat/router';
import { useObserver } from 'mobx-react-lite';
import has from 'lodash/has';
import values from 'lodash/values';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

import useUI from 'hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useProductQueryParams from 'hooks/use-product-query-params';
import { useDispensariesQueryParams } from 'hooks/use-dispensaries-query-params';
import { isBrowser } from 'utils/misc-utils';
import { ProductCardDefaultViewOption } from 'src/gql/graphql';

import { useModals, ModalNames } from 'components/modals';

import {
  setApolloCellId,
  getApolloCellId,
  setApolloCellLoading,
  setCellRoutingEnabled,
} from 'src/utils/apollo/cell-routing';

// special short-hand paths that don't pass up a [path] option
const pathOverrides = {
  '/products/[category]': ({ category }) => ({
    category,
  }),
  '/product/[productId]': ({ productId }) => ({
    product: productId,
  }),
};

export function useCellRoutingController({ dispensary, dispensaryId }) {
  const flags = useFlags();
  const isEnabled = flags['growth.ecomm.platform.cellRouting'];
  setCellRoutingEnabled(!!isEnabled);

  const currentId = getApolloCellId();
  if (!currentId && dispensaryId && !dispensary) {
    setApolloCellLoading();
  }

  if (currentId && !dispensaryId && !dispensary) {
    setApolloCellId(null);
  }

  if (dispensary?.retailer?.enterpriseId) {
    setApolloCellId(dispensary.retailer.enterpriseId);
  }
}

export function useShowPasswordReset({ router }) {
  const { openModal } = useModals();

  useEffect(() => {
    if (router?.query.resetToken) {
      openModal(ModalNames.changePassword);
    }
  }, [openModal, router?.query.resetToken]);
}

export function useShowLogin({ router }) {
  const { openModal } = useModals();

  useEffect(() => {
    if (router?.query.login) {
      openModal(ModalNames.login);
    }
  }, [openModal, router?.query.login]);
}

export function useShowSignup({ router }) {
  const { openModal } = useModals();

  useEffect(() => {
    if (router?.query.signup) {
      openModal(ModalNames.signUp);
    }
  }, [openModal, router?.query.signup]);
}

export function useSetRoutePath({ UI, router, dispensary }) {
  const { asPath, route, query, isReady } = router;

  useEffect(() => {
    if (!isReady) {
      return;
    }
    if (route === UI.route) {
      UI.externalReferrer = true;
    } else {
      UI.externalReferrer = false;
    }

    UI.setPath(asPath, route);
    const skipScrollToTop = !!query?.['dtche[category]'] ?? !!query.category;
    if (!dispensary?.embedSettings?.disablePageLoadsAtTop && !skipScrollToTop) {
      UI.embeddedEventDispatcher(`iframe:scrollToTop`, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath, route, isReady, UI, dispensary, query, route]);

  useEffect(() => {
    if (has(query, `viewOnlyMode`) || has(query, `preview`)) {
      UI.setViewMode(query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, UI]);
}

export function useSetEmbeddedCName({ UI, router }) {
  useEffect(() => {
    if (UI.isEmbedded && router?.query?.cName) {
      UI.setEmbeddedCName(router?.query.cName);
    }
  }, [router?.query?.cName, UI]);
}

export function useDefaultMenuLayout({ UI, dispensary }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(`sm`));
  const [hasSetCustomizationMenuLayout, setHasSetCustomizationMenuLayout] = useState(false);

  const setMenuLayout = useCallback(() => {
    const getMenuLayout = (option) => (option === ProductCardDefaultViewOption.card ? 'cards' : 'list');

    const layoutOption = getMenuLayout(
      isMobile
        ? dispensary.webCustomizationSettingsV2?.productCards?.defaultViewSmall
        : dispensary.webCustomizationSettingsV2?.productCards?.defaultViewLarge ??
            dispensary.storeSettings?.defaultViewStyle
    );

    UI.setMenuLayout(layoutOption);
  }, [UI, isMobile, dispensary]);

  useEffect(() => {
    if (!dispensary) {
      return;
    }

    if (dispensary.id !== UI.lastDispensary?.id) {
      setMenuLayout();
      UI.lastDispensary = dispensary;
      setHasSetCustomizationMenuLayout(false);
    }

    if (!hasSetCustomizationMenuLayout) {
      setMenuLayout();
      setHasSetCustomizationMenuLayout(true);
    }
  }, [UI, dispensary, setMenuLayout, hasSetCustomizationMenuLayout]);
}

export function useUpdateEmbeddedParentWindow({ UI, router }) {
  const { asPath, route, query, isReady } = router;
  const [basePath] = asPath.split('?');
  const isEmbedded = useObserver(() => UI.isEmbedded);
  const { cName } = query;
  const { queryParamsMinusDefaults: productQueryParams } = useProductQueryParams();
  const { queryParamsMinusDefaults: dispensaryQueryParams } = useDispensariesQueryParams();
  const queryParams = { ...productQueryParams, ...dispensaryQueryParams };

  useEffect(() => {
    if (!isEmbedded) {
      return;
    }
    if (!isReady) {
      return;
    }
    const prefix = `/${UI.dispensaryRootUrl}/${cName}`;
    if (basePath.startsWith(prefix)) {
      let embeddedPath = basePath.replace(prefix, '');
      if (embeddedPath.startsWith('/')) {
        embeddedPath = embeddedPath.replace(/^\/+/, '');
      }
      const routeSegment = route.replace(`/${UI.dispensaryRootUrl}/[cName]`, '');
      if (pathOverrides[routeSegment]) {
        const queryData = pathOverrides[routeSegment](query);
        UI.embeddedEventDispatcher('route:changed', { query: { ...queryParams, ...queryData }, cName });
      } else {
        UI.embeddedEventDispatcher('route:changed', { embeddedPath, query: queryParams, cName });
      }
      return;
    }
    let cleanedBasePath = basePath;
    if (cleanedBasePath.startsWith('/')) {
      cleanedBasePath = cleanedBasePath.replace(/^\/+/, '');
    }
    UI.embeddedEventDispatcher('route:changed', { fullPath: cleanedBasePath, query: queryParams });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmbedded, cName, asPath, isReady, values(queryParams).join(), UI]);
}

function useDispensaryRefetcher() {
  const router = useRouter();
  const { cName } = router?.query;
  const { refetch, networkStatus } = useDispensary(cName);
  /*
  don't refetch if we're currently...
   1 - loading a query already
   2 - sort of like loading but related to setVariables
   3 - there's a "fetch more" call in flight
   4 - we're currently refetching already
   5 - ????
   6 - we're polling data
  otherwise refetch
  */
  useEffect(() => {
    if (refetch && networkStatus >= 7) {
      refetch();
    }
  }, [cName, refetch, networkStatus]);
}

function isIframe() {
  if (isBrowser() && window.parent !== window) {
    return true;
  }
  return false;
}

function useIsIframe({ UI }) {
  useEffect(() => {
    UI.isIframe = isIframe();
  }, [UI]);
}

function useSetFirstRender({ UI }) {
  useEffect(() => {
    UI.isFirstRender = false;
  }, [UI]);
}

export default function useUIController() {
  const UI = useUI();
  const router = useRouter();
  const { dispensary, dispensaryId } = useDispensary();

  useCellRoutingController({ dispensary, dispensaryId });
  useSetFirstRender({ UI });
  useSetRoutePath({ UI, router, dispensary });
  useSetEmbeddedCName({ UI, router });
  useDefaultMenuLayout({ UI, dispensary });
  useUpdateEmbeddedParentWindow({ UI, router, dispensary });
  useDispensaryRefetcher();
  useIsIframe({ UI });
  useShowPasswordReset({ router });
  useShowLogin({ router });
  useShowSignup({ router });

  return UI;
}
